import React from 'react';
import PropTypes from 'prop-types';
import { Gap, Icon, Spacing, Text, Box } from '@reservamos/elements';
import 'styles/components/SelectedSeats';
import { useTranslation } from 'react-i18next';
import petBadge from 'images/badges/pet.svg';

/**
 * HeaderDiagramBus component.
 * It shows the selected seats on the bus diagram page.
 * Also displays information about pet friendly, women seats and the legend.
 * @param {object} props - legend, hideLegend, showPetFriendly, handlePetDialogShowPress, floor, secondFloor.
 * @param {string} props.legend - Custom legend text.
 * @param {boolean} props.hideLegend - Should show or hide the legend.
 * @param {boolean} props.showPetFriendly - Should show or hide the pet friendly information.
 * @param {function} props.handlePetDialogShowPress - Callback when the pet friendly information is pressed.
 * @param {string} props.floor - Floor number ('1' for single decker or '2' for double decker).
 * @param {boolean} props.hasSecondFloor - Whether the bus has a second floor.
 * @returns - HeaderDiagramBus component.
 */
const HeaderDiagramBus = ({
  legend,
  hideLegend,
  showPetFriendly,
  handlePetDialogShowPress,
  floor,
  hasSecondFloor,
}) => {
  const { t } = useTranslation('seats');
  const floorsLabel = floor === '2' ? t('2nd_floor') : t('1st_floor');

  /**
   * Gets the appropriate header title based on legend and floor info.
   * @returns {string} The header title text.
   */
  const getHeaderTitle = () => {
    if (legend) return legend;
    if (hasSecondFloor) return `${t('your_seats')} - ${floorsLabel}`;

    return t('your_seats');
  };

  return (
    <div className="selected-seats">
      <Spacing vertical size="S">
        <Text weight="bold" fontFamily="secondary">
          {getHeaderTitle()}
        </Text>
        {hideLegend || <Text size="S">{t('chosee_to_seats')}</Text>}
        {showPetFriendly && (
          <Gap alignItems="center">
            <a onClick={handlePetDialogShowPress}>
              <Box
                paddingHorizontal="XS"
                paddingVertical="XS"
                border="all"
                borderColor="primary"
                borderRadius="L"
                bgColor=""
              >
                <Gap alignItems="center">
                  <Icon type={petBadge} size="S" />
                  <Text weight="semibold" size="S">
                    {t('pet_dialog.btn_info')}
                  </Text>
                </Gap>
              </Box>
            </a>
          </Gap>
        )}
      </Spacing>
    </div>
  );
};

HeaderDiagramBus.propTypes = {
  hideLegend: PropTypes.bool,
  showPetFriendly: PropTypes.bool,
  handlePetDialogShowPress: PropTypes.func.isRequired,
  legend: PropTypes.string,
  floor: PropTypes.string,
  hasSecondFloor: PropTypes.bool,
};

HeaderDiagramBus.defaultProps = {
  hideLegend: false,
  showPetFriendly: false,
  legend: '',
  floor: '1',
  hasSecondFloor: false,
};

export default HeaderDiagramBus;
